import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";

import type { FacilityResponse, OrgResponse } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";

import { orgBusinessName } from "@/shared/orgDisplay";

import {
  formatFacilityService,
  formatStaffCheckIn,
  type FacilityRead,
} from "./facilityDetailFormatters";

function SnapshotFact({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, mt: 0.5 }}>
        {children}
      </Box>
    </Box>
  );
}

export function FacilitySnapshotPanel({
  facility,
  organization,
}: {
  facility: FacilityResponse;
  organization: FacilityRead<OrgResponse>;
}) {
  const services = facility.enabled_services ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="facility-snapshot-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}
    >
      <Stack spacing={2}>
        <Typography
          id="facility-snapshot-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Facility snapshot
        </Typography>
        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            m: 0,
          }}
        >
          <SnapshotFact label="Parent organization">
            {organization.state === "loading" ? (
              <Box
                role="status"
                aria-label="Loading parent organization"
                aria-live="polite"
                sx={{ display: "flex", alignItems: "center", minHeight: 28 }}
              >
                <CircularProgress size={20} />
              </Box>
            ) : organization.state === "error" ? (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={organization.onRetry}
                  >
                    Retry
                  </Button>
                }
              >
                Parent organization could not be loaded. Try again.
              </Alert>
            ) : organization.data ? (
              <Link
                component={RouterLink}
                to={`/organizations/${organization.data.org_id}`}
                variant="body2"
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {orgBusinessName(organization.data)}
              </Link>
            ) : null}
          </SnapshotFact>
          <SnapshotFact label="Staff check-in support">
            <Typography variant="body2">
              {formatStaffCheckIn(facility.staff_check_in_available)}
            </Typography>
          </SnapshotFact>
          <SnapshotFact label="Created">
            <Typography variant="body2">
              {formatDate(facility.created_at)}
            </Typography>
          </SnapshotFact>
          <SnapshotFact label="Services offered">
            {services.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No services selected.
              </Typography>
            ) : (
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                {services.map((service) => (
                  <Chip
                    key={service}
                    size="small"
                    variant="outlined"
                    label={formatFacilityService(service)}
                  />
                ))}
              </Stack>
            )}
          </SnapshotFact>
        </Box>
      </Stack>
    </Paper>
  );
}
